import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, MailIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { MEMBER_PROFILES } from '../data/member-profiles';
export function OurPeopleStories() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, {
    once: true,
    margin: '-50px'
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };
  return <section ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden" data-id="element-3277">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-3278">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-haa-skyblue/5 rounded-full blur-3xl" data-id="element-3279" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 bg-haa-midblue/5 rounded-full blur-3xl" data-id="element-3280" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3281">
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex flex-col items-center" data-id="element-3282">
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12 max-w-3xl" data-id="element-3283">
            <h2 className="font-bebas text-5xl md:text-6xl text-haa-navy tracking-wider mb-4" data-id="element-3284">
              {isEs ? 'Nuestra Gente, Nuestras Historias' : 'Our People Our Stories'}
            </h2>
            <p className="font-nunito text-lg text-gray-600" data-id="element-3285">
              {isEs ? 'Conoce a los hondureño-americanos que están generando un impacto en todo Estados Unidos, liderando en los negocios, el servicio público y la comunidad.' : 'Meet the Honduran-Americans making an impact across the United States, leading in business, public service, and community.'}
            </p>
          </motion.div>

          {/* Scroll Controls */}
          <motion.div variants={itemVariants} className="w-full relative" data-id="element-3286">
            {/* Arrow buttons */}
            <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 shadow-lg border border-gray-200 flex items-center justify-center text-haa-navy hover:bg-haa-blue hover:text-white transition-colors -ml-2 sm:-ml-4 backdrop-blur-sm" aria-label={isEs ? 'Anterior' : 'Previous'} data-id="element-3287">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-3288" />
            </button>
            <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 shadow-lg border border-gray-200 flex items-center justify-center text-haa-navy hover:bg-haa-blue hover:text-white transition-colors -mr-2 sm:-mr-4 backdrop-blur-sm" aria-label={isEs ? 'Siguiente' : 'Next'} data-id="element-3289">
              <ChevronRightIcon className="w-5 h-5" data-id="element-3290" />
            </button>

            {/* Edge fade masks */}
            <div className="relative w-full" style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)'
          }} data-id="element-3291">
              {/* Scrollable row */}
              <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }} data-id="element-3292">
                {MEMBER_PROFILES.map(member => <motion.a key={member.slug} href={`#/member/${member.slug}`} variants={itemVariants} className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col flex-shrink-0 snap-start" style={{
                width: '300px',
                minWidth: '300px'
              }} data-id="element-3293">
                    {/* Image Container */}
                    <div className="relative h-56 overflow-hidden bg-gray-100" data-id="element-3294">
                      <img src={member.photo} alt={member.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" data-id="element-3295" />
                      <div className="absolute inset-0 bg-gradient-to-t from-haa-navy/80 via-haa-navy/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" data-id="element-3296" />

                      {/* Badge */}
                      <div className="absolute top-4 right-4" data-id="element-3297">
                        <span className="inline-block px-3 py-1 bg-haa-skyblue text-white font-bebas text-sm tracking-wider rounded-full shadow-sm" data-id="element-3298">
                          {isEs ? member.badgeEs : member.badge}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5 flex flex-col flex-grow" data-id="element-3299">
                      <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-1 group-hover:text-haa-blue transition-colors" data-id="element-3300">
                        {member.name}
                      </h3>
                      <p className="font-nunito text-haa-midblue font-semibold text-xs uppercase tracking-wider mb-3" data-id="element-3301">
                        {isEs && member.roleEs ? member.roleEs : member.role}
                      </p>

                      <div className="mt-auto pt-3 flex items-center text-haa-blue font-nunito font-bold text-sm group-hover:text-haa-skyblue transition-colors" data-id="element-3302">
                        {isEs ? 'Ver Perfil' : 'View Profile'}
                        <ArrowRightIcon className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-3303" />
                      </div>
                    </div>
                  </motion.a>)}
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div variants={itemVariants} className="bg-white px-8 py-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-6 w-full max-w-3xl mt-12" data-id="element-3304">
            <div className="text-center sm:text-left" data-id="element-3305">
              <h4 className="font-bebas text-2xl text-haa-navy tracking-wider mb-1" data-id="element-3306">
                {isEs ? '¿Conoces a alguien con una gran historia?' : 'Know someone with a great story?'}
              </h4>
              <p className="font-nunito text-gray-500 text-sm" data-id="element-3307">
                {isEs ? 'Queremos destacar las voces de nuestra comunidad.' : 'We want to highlight voices from our community.'}
              </p>
            </div>
            <a href="mailto:info@honduranamerican.org" className="inline-flex items-center gap-2 bg-haa-navy text-white font-nunito font-bold text-sm px-6 py-3 rounded-full hover:bg-haa-blue transition-colors whitespace-nowrap" data-id="element-3308">
              <MailIcon className="w-4 h-4" data-id="element-3309" />
              {isEs ? 'Contáctanos' : 'Reach Out to Us'}
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}